import { useContext, useState } from "react";

import MUIBox from "@mui/material/Box";
import MUIButton from "@mui/material/Button";
import MUITooltip from "@mui/material/Tooltip";
import MUICheckbox from "@mui/material/Checkbox";
import MUIDialog from "@mui/material/Dialog";
import MUIDialogActions from "@mui/material/DialogActions";
import MUIDialogContent from "@mui/material/DialogContent";
import MUIDialogTitle from "@mui/material/DialogTitle";
import MUIFormControlLabel from "@mui/material/FormControlLabel";
import MUIStack from "@mui/material/Stack";
import MUITextField from "@mui/material/TextField";
import MUITable from "@mui/material/Table";
import MUITableContainer from "@mui/material/TableContainer";
import MUITableRow from "@mui/material/TableRow";
import MUITableCell from "@mui/material/TableCell";
import MUITableHead from "@mui/material/TableHead";
import MUITableBody from "@mui/material/TableBody";
import MUIPaper from "@mui/material/Paper";
import MUIChip from "@mui/material/Chip";
import MUICheckIcon from "@mui/icons-material/Check";
import MUIWidgetIcon from "@mui/icons-material/Widgets";
import { enqueueSnackbar as NotistackEnqueueSnackbar } from "notistack";

import { ChartContext } from "../../../Context/ChartContext/ChartContext";
import MainContext from "../../../Context/MainContext/MainContext";
import Card from "../../Card";
import EmptyState from "../../EmptyState";
import Term from "../../../Utility/Crud/Term";
import ProductSubType from "../../../Utility/Crud/ProductSubType";
import Format from "../../../Utility/Format";
import OrderActivity from "../../../Utility/Crud/OrderActivity";
import Provider from "../../../Utility/Crud/Provider";
import CreateQuoteModalFromTicket from "../../../Seating/Patient/PatientViewComponents/CreateQuoteModalFromTicket";

/**
 * Renders a Card component displaying service parts for a ticket or accessory order.
 * @returns {JSX.Element}
 */

const title = "Service Parts";
const icon = MUIWidgetIcon;

const className = "CardChartServiceParts";

export default function CardChartServiceParts(props) {
  const chartContext = useContext(ChartContext);
  const mainContext = useContext(MainContext);
  const order = chartContext.chart.orderActivity[chartContext.orderId];
  const parentOrder = OrderActivity.getParentOrder(
    chartContext.orderId,
    chartContext.chart,
  );
  const parentOrderPrimaryProduct = OrderActivity.getPrimaryProduct(
    OrderActivity.getProducts(parentOrder, chartContext.chart),
  );
  const currentServiceQuote = Object.values(chartContext.chart.serviceQuote)
    .filter((serviceQuote) => serviceQuote.orderId === chartContext.orderId)
    .sort((firstServiceQuote, secondServiceQuote) => {
      const firstTimestamp = new Date(
        firstServiceQuote.updatedOn || firstServiceQuote.createdOn,
      ).getTime();
      const secondTimestamp = new Date(
        secondServiceQuote.updatedOn || secondServiceQuote.createdOn,
      ).getTime();
      return (
        secondTimestamp - firstTimestamp ||
        secondServiceQuote.id - firstServiceQuote.id
      );
    })[0];
  const quoteDialogServiceProducts = currentServiceQuote?.documentData
    ? JSON.parse(currentServiceQuote.documentData)
        .map((documentDatum) => {
          const servicePartCatalog =
            chartContext.chart.servicePartCatalog[documentDatum.ItemId];
          if (!servicePartCatalog) {
            return null;
          }
          return {
            ...servicePartCatalog,
            reason: documentDatum.Reason,
            qty: documentDatum.Qty,
            warranty: documentDatum.Warranty,
            thirtyDayFit: documentDatum.ThirtyDayFit,
            abnPartText: `${servicePartCatalog.description.replace(/,/g, "")} ${(servicePartCatalog.hcpc || "").replace(/,/g, "")}`,
          };
        })
        .filter((serviceProduct) => serviceProduct !== null)
    : [];
  const approval =
    chartContext.chart.approval[
      Object.values(chartContext.chart.orderActivityApprovals).find(
        (chartOrderActivityApproval) =>
          chartOrderActivityApproval.orderActivityId === chartContext?.orderId,
      )?.approvalsId
    ];
  const selectedProvider = order.orderingProviderId
    ? chartContext.chart.provider[order.orderingProviderId]
    : null;
  let providerNotFoundData = null;
  if (
    order.orderingProviderNotFound &&
    order.jsonOrderingProviderNotFoundData
  ) {
    try {
      providerNotFoundData = JSON.parse(order.jsonOrderingProviderNotFoundData);
    } catch {
      providerNotFoundData = null;
    }
  }
  const providerInformation = selectedProvider
    ? {
        title: Provider.getDetailedDisplayName(selectedProvider),
        npi: selectedProvider.npi,
      }
    : providerNotFoundData
      ? {
          title: [
            providerNotFoundData.name,
            [providerNotFoundData.city, providerNotFoundData.state]
              .filter(Boolean)
              .join(", "),
            providerNotFoundData.npi,
          ]
            .filter(Boolean)
            .join(" - "),
          npi: providerNotFoundData.npi,
        }
      : null;
  const hasValidProvider = Boolean(
    order.orderingProviderId ||
    (providerNotFoundData &&
      providerNotFoundData.name &&
      providerNotFoundData.name.trim().length > 0 &&
      providerNotFoundData.npi &&
      providerNotFoundData.npi.length === 10 &&
      /^\d+$/.test(providerNotFoundData.npi)),
  );
  const isAccessoryAfterSale = order.orderType === "Accessory After Sale";
  const isService = order.orderType === "Service";
  const canAddPartsToAccessoryAfterSale =
    isAccessoryAfterSale && ![2, 3, 4].includes(order.status);
  const canAddPartsToService = isService && order.orderStatusReasonId === 72;
  const isQuoteWorkflow =
    canAddPartsToAccessoryAfterSale || canAddPartsToService;
  const [showAddPartsDialog, setShowAddPartsDialog] = useState(false);
  const [isAddPartsDialogMinimized, setIsAddPartsDialogMinimized] =
    useState(false);
  const [showAbnDialog, setShowAbnDialog] = useState(false);
  const [selectedAbnParts, setSelectedAbnParts] = useState([]);
  const [abnAmount, setAbnAmount] = useState("");
  const [abnRequiredNote, setAbnRequiredNote] = useState("");

  /**
   * Opens the ABN dialog with the current chart values.
   * @returns {void}
   */
  const handleOpenAbnDialog = () => {
    setSelectedAbnParts(
      order.abnProductOther ? order.abnProductOther.split(",") : [],
    );
    setAbnAmount(order.abnAmount === null ? "" : order.abnAmount.toString());
    setAbnRequiredNote(approval.abnRequiredNote || "");
    setShowAbnDialog(true);
  };

  /**
   * Toggles whether a service part requires an ABN.
   * @param {string} abnPartText - The persisted service-part text.
   * @returns {void}
   */
  const handleToggleAbnPart = (abnPartText) => {
    if (selectedAbnParts.includes(abnPartText)) {
      setSelectedAbnParts(
        selectedAbnParts.filter(
          (selectedAbnPart) => selectedAbnPart !== abnPartText,
        ),
      );
      return;
    }

    setSelectedAbnParts([...selectedAbnParts, abnPartText]);
  };

  /**
   * Adds the ABN edits to chart state for the normal chart save flow.
   * @returns {void}
   */
  const handleApplyAbnInformation = () => {
    const parsedAbnAmount = Number(abnAmount);
    if (
      selectedAbnParts.length > 0 &&
      (!abnRequiredNote.trim() || !parsedAbnAmount)
    ) {
      NotistackEnqueueSnackbar("ABN amount and reason are required", {
        variant: "warning",
      });
      return;
    }

    chartContext.onChange({
      orderActivity: {
        [chartContext.orderId]: {
          abnProductOther: selectedAbnParts.join(",").substring(0, 254),
          abnAmount: selectedAbnParts.length > 0 ? parsedAbnAmount : null,
        },
      },
      approval: {
        [approval.id]: {
          abnRequiredNote:
            selectedAbnParts.length > 0 ? abnRequiredNote.trim() : "",
        },
      },
    });
    setShowAbnDialog(false);
    NotistackEnqueueSnackbar(
      "ABN information updated. Save the chart to apply changes.",
      { variant: "info" },
    );
  };

  /**
   * Closes the add-parts dialog and restores its full-size state.
   * @returns {void}
   */
  const handleCloseAddPartsDialog = () => {
    setShowAddPartsDialog(false);
    setIsAddPartsDialogMinimized(false);
  };

  /**
   * Reloads the chart after the quote workflow finishes.
   * @returns {Promise<void>}
   */
  const handleRefreshAfterQuote = async () => {
    handleCloseAddPartsDialog();
    await chartContext.reloadChartData();
  };

  /**
   * Gets the actions displayed in the card header.
   * @returns {JSX.Element[]|null} The card actions.
   */
  const getActions = () => {
    if (!isQuoteWorkflow) {
      return null;
    }

    const isDisabled =
      chartContext.hasPendingChanges ||
      chartContext.isUpdating ||
      !hasValidProvider;
    let tooltip = "";
    if (chartContext.hasPendingChanges) {
      tooltip = "Save your chart changes before adding parts";
    } else if (!hasValidProvider) {
      tooltip = "Ordering provider required before adding parts";
    }

    const actions = [];
    if (quoteDialogServiceProducts.length > 0) {
      actions.push(
        <MUIButton
          variant="outlined"
          size="small"
          disabled={chartContext.isUpdating}
          onClick={handleOpenAbnDialog}
        >
          ABN Info
        </MUIButton>,
      );
    }

    actions.push(
      <MUITooltip title={tooltip}>
        <span>
          <MUIButton
            variant="contained"
            size="small"
            disabled={isDisabled}
            onClick={() => {
              setShowAddPartsDialog(true);
              setIsAddPartsDialogMinimized(false);
            }}
          >
            {isAddPartsDialogMinimized ? "Reopen Quote" : "Add Parts"}
          </MUIButton>
        </span>
      </MUITooltip>,
    );
    return actions;
  };

  // Render part table
  const renderTable = () => {
    // Get serviceQuote for current order
    const serviceQuote = structuredClone(currentServiceQuote);
    let serviceParts = [];
    if (serviceQuote?.documentData?.length > 0) {
      serviceQuote.documentData = JSON.parse(serviceQuote.documentData);

      // Build parts array with only necessary fields
      serviceParts = serviceQuote.documentData.map((documentDatum) => {
        const catalogItem =
          chartContext.chart.servicePartCatalog[documentDatum.ItemId] || {};
        return {
          servicePartCatalogCategoryTermId:
            catalogItem.servicePartCatalogCategoryTermId,
          name: catalogItem.description,
          msrp: catalogItem.msrp,
          hcpcs: catalogItem.hcpc,
          quantity: documentDatum.Qty,
          warranty: documentDatum.Warranty,
          thirtyDayFit: documentDatum.ThirtyDayFit,
          abnRequired: documentDatum.AbnRequired,
          detail: documentDatum.Reason,
        };
      });
    }

    // const orderActivityProducts = Object.values(chartContext.chart.orderActivityProducts)
    // .filter(orderActivityProduct => orderActivityProduct.orderActivityId === chartContext.orderId);
    // const products = Object.values(chartContext.chart.product || {})
    // .filter(product => orderActivityProducts.some(orderActivityProduct => orderActivityProduct.productsId === product.id));

    // This is the "Generic Parts" section. Removing at Heather's request...can readd if needed.
    // let productParts = [];
    // if (products.length > 0) {
    //     productParts = products
    //         .map(product => {
    //             return {
    //                 servicePartCatalogCategoryTermId: null,
    //                 name: ProductSubType.getDisplayName(mainContext.productSubTypesIndexed?.[product.productSubTypeId]),
    //                 hcpcs: product.hcpcs,
    //                 quantity: 1,
    //                 warranty: false,
    //                 thirtyDayFit: false,
    //                 abnRequired: false,
    //                 detail: ""
    //             };
    //         });
    // }
    // const allParts = [...serviceParts, ...productParts];

    const allParts = serviceParts;

    // Calculate labor units from all parts (not just K0739)
    const laborUnits = calculateAdditionalLaborUnits(allParts, chartContext);

    if (allParts.length === 0) {
      return <EmptyState line1="No service parts" />;
    } else {
      // Sort parts by category name, then by hcpcs, with K0739 at the bottom
      const sortedParts = [...allParts].sort((a, b) => {
        // Move K0739 to the bottom
        if (a.hcpcs === "K0739" && b.hcpcs !== "K0739") {
          return 1;
        }
        if (a.hcpcs !== "K0739" && b.hcpcs === "K0739") {
          return -1;
        }

        const aCategory =
          Term.getDisplayName(
            mainContext.termsIndexed[a.servicePartCatalogCategoryTermId],
          ) || "";
        const bCategory =
          Term.getDisplayName(
            mainContext.termsIndexed[b.servicePartCatalogCategoryTermId],
          ) || "";
        const categoryCompare = aCategory.localeCompare(bCategory, undefined, {
          sensitivity: "base",
        });
        if (categoryCompare !== 0) {
          return categoryCompare;
        }

        // Secondary sort by hcpcs
        return (a.hcpcs || "").localeCompare(b.hcpcs || "", undefined, {
          sensitivity: "base",
        });
      });

      function checkForABNRequired(part) {
        if (!order?.abnProductOther || !part.name) {
          return false;
        }
        const partName =
          part.name.replace(/,/g, "") +
          " " +
          (part.hcpcs ? part.hcpcs.replace(/,/g, "") : "");
        const abnArray = order.abnProductOther.split(",");
        return abnArray.includes(partName);
      }

      return (
        <MUITableContainer component={MUIPaper} elevation={0} sx={{ mt: 1 }}>
          <MUITable size="small">
            <MUITableHead
              sx={{
                backgroundColor: (theme) => theme.palette.background.default,
              }}
            >
              <MUITableRow>
                <MUITableCell sx={{ width: "120px" }}>Category</MUITableCell>
                <MUITableCell>Description</MUITableCell>
                <MUITableCell sx={{ width: "80px" }}>MSRP</MUITableCell>
                <MUITableCell sx={{ width: "80px", align: "right" }}>
                  HCPCS
                </MUITableCell>
                <MUITableCell sx={{ width: "60px" }}>Qty</MUITableCell>
                {props.showWarrantyColumn !== false ? (
                  <MUITableCell sx={{ width: "60px" }}>Warranty</MUITableCell>
                ) : null}
                {props.showThirtyDayFitColumn !== false ? (
                  <MUITableCell sx={{ width: "60px" }}>30D</MUITableCell>
                ) : null}
                <MUITableCell sx={{ width: "60px" }}>ABN</MUITableCell>
                <MUITableCell sx={{ width: "120px" }}>Detail</MUITableCell>
              </MUITableRow>
            </MUITableHead>
            <MUITableBody>
              {sortedParts.map((part, i) => (
                <MUITableRow key={i}>
                  <MUITableCell>
                    <MUIChip
                      icon={<MUIWidgetIcon fontSize="small" />}
                      size="small"
                      label={
                        part.hcpcs === "K0739"
                          ? "Estimated Labor"
                          : Term.getDisplayName(
                              mainContext.termsIndexed[
                                part.servicePartCatalogCategoryTermId
                              ],
                            ) || "None"
                      }
                      sx={{
                        height: "26px",
                        "& .MuiChip-label": { px: 1.5 },
                        backgroundColor: (theme) =>
                          theme.palette.background.default,
                      }}
                    />
                  </MUITableCell>
                  <MUITableCell>{part.name}</MUITableCell>
                  <MUITableCell sx={{ textAlign: "right" }}>
                    {Format.currency(part.msrp)}
                  </MUITableCell>
                  <MUITableCell>{part.hcpcs}</MUITableCell>
                  <MUITableCell>
                    {part.hcpcs === "K0739"
                      ? `${laborUnits * 15}m`
                      : part.quantity}
                  </MUITableCell>
                  {props.showWarrantyColumn !== false ? (
                    <MUITableCell align="center">
                      {part.warranty ? (
                        <MUICheckIcon
                          fontSize="small"
                          sx={{ color: (theme) => theme.palette.success.main }}
                        />
                      ) : (
                        ""
                      )}
                    </MUITableCell>
                  ) : null}
                  {props.showThirtyDayFitColumn !== false ? (
                    <MUITableCell align="center">
                      {part.thirtyDayFit ? (
                        <MUICheckIcon
                          fontSize="small"
                          sx={{ color: (theme) => theme.palette.success.main }}
                        />
                      ) : (
                        ""
                      )}
                    </MUITableCell>
                  ) : null}
                  <MUITableCell align="center">
                    {checkForABNRequired(part) ? (
                      <MUICheckIcon
                        fontSize="small"
                        sx={{ color: (theme) => theme.palette.success.main }}
                      />
                    ) : (
                      ""
                    )}
                  </MUITableCell>
                  <MUITableCell>{part.detail}</MUITableCell>
                </MUITableRow>
              ))}
            </MUITableBody>
          </MUITable>
        </MUITableContainer>
      );
    }
  };

  /**
   * Calculates how many units should be on the labor line item (if present)
   * by looking at all of the service parts and summing up their max billable
   * units. Return whatever number of units (0, 1, or 2) are needed to make
   * that sum reach 2.
   * @param {Array} parts - All parts for the order
   * @param {Object} chartContext
   * @returns {number}
   */
  function calculateAdditionalLaborUnits(parts, chartContext) {
    let totalMaxBillableUnits = 0;

    parts.forEach((part) => {
      // Find productSubType by matching hcpcs
      const matchingProductSubType = Object.values(
        mainContext.productSubTypesIndexed,
      ).find((productSubType) => productSubType.hcpcs === part.hcpcs);
      if (
        matchingProductSubType &&
        matchingProductSubType.maxBillableLaborUnits
      ) {
        totalMaxBillableUnits += Number(
          matchingProductSubType.maxBillableLaborUnits,
        );
      }
    });

    // Return units needed to reach 2 (never negative)
    return Math.max(0, 2 - totalMaxBillableUnits);
  }

  /**
   * Renders the Add Parts dialog.
   * @returns {JSX.Element|null} Add Parts dialog when open
   */
  const renderAddPartsDialog = () => {
    if (!showAddPartsDialog) {
      return null;
    }

    return (
      <CreateQuoteModalFromTicket
        baseUnitInfo={{
          makeModel: parentOrder ? parentOrder.currentModel : "",
          serial: parentOrder ? parentOrder.serialNumber : "",
          hcpcs: parentOrderPrimaryProduct
            ? parentOrderPrimaryProduct.hcpcs
            : "",
        }}
        refresh={handleRefreshAfterQuote}
        parentOrder={parentOrder ? parentOrder.id : null}
        serviceProducts={quoteDialogServiceProducts}
        order={order}
        currentFullOrder={order}
        onToggle={handleCloseAddPartsDialog}
        patient={chartContext.chart.patient[chartContext.patientId]}
        onSmall={() => setIsAddPartsDialogMinimized(true)}
        small={isAddPartsDialogMinimized}
        providerInformation={providerInformation}
        showWarrantyColumn={props.showWarrantyColumn}
        showThirtyDayFitColumn={props.showThirtyDayFitColumn}
        showEstimatedLabor={props.showEstimatedLabor}
      />
    );
  };

  /**
   * Renders the ABN Information dialog.
   * @returns {JSX.Element} ABN Information dialog
   */
  const renderAbnDialog = () => {
    return (
      <MUIDialog
        open={showAbnDialog}
        onClose={() => setShowAbnDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <MUIDialogTitle>ABN Information</MUIDialogTitle>
        <MUIDialogContent>
          <MUIStack spacing={2} sx={{ pt: 1 }}>
            {quoteDialogServiceProducts.map((serviceProduct) => {
              return (
                <MUIFormControlLabel
                  key={serviceProduct.id}
                  control={
                    <MUICheckbox
                      checked={selectedAbnParts.includes(
                        serviceProduct.abnPartText,
                      )}
                      onChange={() =>
                        handleToggleAbnPart(serviceProduct.abnPartText)
                      }
                    />
                  }
                  label={serviceProduct.abnPartText}
                />
              );
            })}
            <MUITextField
              label="ABN Amount"
              type="number"
              value={abnAmount}
              onChange={(event) => setAbnAmount(event.target.value)}
              required={selectedAbnParts.length > 0}
            />
            <MUITextField
              label="ABN Required Reason"
              value={abnRequiredNote}
              onChange={(event) => setAbnRequiredNote(event.target.value)}
              required={selectedAbnParts.length > 0}
              multiline
              rows={3}
            />
          </MUIStack>
        </MUIDialogContent>
        <MUIDialogActions>
          <MUIButton onClick={() => setShowAbnDialog(false)}>Cancel</MUIButton>
          <MUIButton variant="contained" onClick={handleApplyAbnInformation}>
            Apply
          </MUIButton>
        </MUIDialogActions>
      </MUIDialog>
    );
  };

  /**
   * Renders the content of the card.
   * @returns {JSX.Element} Card content and dialogs
   */
  const renderContent = () => (
    <>
      <MUIBox>{renderTable()}</MUIBox>
      {renderAddPartsDialog()}
      {renderAbnDialog()}
    </>
  );

  return (
    <Card
      renderContent={renderContent}
      getTitle={() => title}
      getIcon={() => icon}
      getActions={getActions}
    />
  );
}

CardChartServiceParts.meta = { className: className, title: title, icon: icon };
